/**
 * O relatório de uma página — o pacote já calculado, renderizado e imprimível.
 *
 * **Abre pela conclusão:** de quem é a carteira, depois quem mais deve, depois o que mudou, e só
 * então os achados com evidência. Quem lê um relatório quer saber de quem é a carteira antes de
 * saber quantos títulos ela tem.
 *
 * O front não recalcula nada: a rota devolve o pacote pronto. Isso não é economia de CPU — é a
 * garantia de que o número impresso e o número da tela são o mesmo número, calculado uma vez.
 *
 * Imprimir usa o `window.print` e um `@media print` no CSS: sem gerador de PDF no servidor, sem
 * uma segunda implementação do layout que divergiria da primeira no primeiro ajuste.
 */
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Barras, FaixaParticipacao, Indicador } from '@/components/graficos';
import { frase } from '@/lib/achados/frases';
import type { Achado } from '@/lib/achados/tipos';
import { chaveRef } from '@/lib/achados/tipos';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Spinner } from '@/components/ui/misc';
import { api } from '@/lib/api';
import { rotaEntidade } from '@/lib/recursos';

type Cedente = { rotulo: string; rota: string };

type Relatorio = {
  data_referencia: string;
  limiar: number;
  exposicao: {
    titulos: number;
    valor_nominal: number;
    valor_presente: number;
    cedentes: number;
    nucleos: number;
    grupos: {
      rotulo: string;
      fatia: number;
      valor_nominal: number;
      valor_vencido: number;
      valor_intragrupo: number;
      sacados: number;
      cedentes: Cedente[];
    }[];
  };
  maiores_sacados: {
    rotulo: string;
    documentos: number;
    titulos: number;
    valor_nominal: number;
    fatia: number;
  }[];
  mudanca: {
    de: string;
    para: string;
    entraram: number;
    sairam: number;
    rolaram: number;
    valor_rolado: number;
  } | null;
  achados: Achado[];
  rotulos: Record<string, string>;
};

const reais = (v: number) =>
  v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 });
const pct = (v: number) => `${v.toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`;
const dia = (iso: string) => iso.split('-').reverse().join('/');

export function PainelRelatorioCarteira({ carteiraId }: { carteiraId: string }) {
  const [rel, setRel] = useState<Relatorio | null>(null);
  const [carregando, setCarregando] = useState(true);

  const carregar = useCallback(async () => {
    setCarregando(true);
    const posicoes = await api.get<{ id: string }[]>(
      `/cadastro/carteiras/${carteiraId}/posicoes`,
    );
    if (!posicoes.ok || !posicoes.dados.length) {
      setCarregando(false);
      if (!posicoes.ok) toast.error(posicoes.erro);
      return;
    }
    const r = await api.get<Relatorio>(
      `/cadastro/carteiras/posicoes/${posicoes.dados[0].id}/relatorio`,
    );
    setCarregando(false);
    if (r.ok) setRel(r.dados);
    else toast.error(r.erro);
  }, [carteiraId]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  if (carregando) return <Spinner />;
  if (!rel) {
    return (
      <Card>
        <CardContent className="pt-6">
          <p className="tipo-corpo-pequeno text-text-tertiary">
            Nenhuma posição importada ainda — não há relatório a montar.
          </p>
        </CardContent>
      </Card>
    );
  }

  const e = rel.exposicao;
  const fortes = rel.achados.filter((a) => a.forca !== 'fraco');
  const fracos = rel.achados.filter((a) => a.forca === 'fraco');

  const alvoDe = (a: Achado) => (i: number) => {
    const ref = a.entidades[i];
    if (!ref) return null;
    return (
      <Link to={rotaEntidade(ref[0], ref[1])} className="font-semibold text-accent hover:underline">
        {rel.rotulos[chaveRef(ref)] ?? ref[1]}
      </Link>
    );
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <CardTitle>Relatório — posição de {dia(rel.data_referencia)}</CardTitle>
          <Button size="sm" variant="fantasma" onClick={() => window.print()}>
            Imprimir
          </Button>
        </div>
        <p className="text-[13px] leading-[1.5] text-text-secondary">
          {/* A frase honesta: nunca «14 viraram 2», que afirmaria uma fusão que não houve. */}
          <span className="font-semibold text-text">
            {e.cedentes} cedentes, {e.nucleos}{' '}
            {e.nucleos === 1 ? 'grupo econômico' : 'grupos econômicos'}
          </span>{' '}
          — {reais(e.valor_nominal)} em {e.titulos} títulos. Agrupados sob a régua{' '}
          {rel.limiar.toFixed(2).replace('.', ',')}: mudar a régua muda o agrupamento, e por isso
          ela está dita.
        </p>
      </CardHeader>

      <CardContent className="flex flex-col gap-6">
        <section className="flex flex-col gap-3">
          <p className="tipo-rotulo text-text-tertiary">1 · De quem é esta carteira</p>
          <FaixaParticipacao
            fatias={e.grupos.map((g) => ({ rotulo: g.rotulo, valor: g.valor_nominal }))}
          />
          <div className="flex flex-col">
            {e.grupos.map((g, i) => (
              <div key={i} className="border-t border-border py-2 first:border-t-0">
                <p className="flex flex-wrap items-baseline gap-x-2">
                  <span className="tipo-dado font-semibold text-text">{pct(g.fatia)}</span>
                  <span className="text-text">{g.rotulo}</span>
                  <span className="tipo-corpo-pequeno text-text-tertiary">
                    {reais(g.valor_nominal)} · {g.sacados} sacados
                  </span>
                </p>
                {g.cedentes.length > 1 && (
                  <p className="tipo-corpo-pequeno text-text-tertiary">
                    {g.cedentes.map((c, j) => (
                      <span key={j}>
                        {j > 0 && ' · '}
                        <Link to={c.rota} className="text-accent hover:underline">
                          {c.rotulo}
                        </Link>
                      </span>
                    ))}
                  </p>
                )}
                {g.valor_intragrupo > 0 && (
                  <p className="tipo-corpo-pequeno text-warning">
                    {reais(g.valor_intragrupo)} com o sacado dentro do próprio grupo
                  </p>
                )}
              </div>
            ))}
          </div>
        </section>

        <section className="flex flex-col gap-2">
          <p className="tipo-rotulo text-text-tertiary">2 · Quem mais deve</p>
          <Barras
            itens={rel.maiores_sacados.map((s) => ({
              rotulo:
                s.documentos > 1 ? `${s.rotulo} (${s.documentos} CNPJs da mesma raiz)` : s.rotulo,
              valor: s.valor_nominal,
              texto: `${reais(s.valor_nominal)} · ${pct(s.fatia)}`,
            }))}
            vazio="Sem títulos nesta posição."
          />
        </section>

        <section className="flex flex-col gap-2">
          <p className="tipo-rotulo text-text-tertiary">3 · O que mudou</p>
          {rel.mudanca ? (
            <div className="flex flex-wrap gap-x-8 gap-y-3">
              <Indicador rotulo="Entraram" valor={rel.mudanca.entraram} />
              <Indicador rotulo="Saíram" valor={rel.mudanca.sairam} />
              <Indicador
                rotulo="Rolaram"
                valor={rel.mudanca.rolaram}
                detalhe={`${reais(rel.mudanca.valor_rolado)} empurrados`}
                alerta={rel.mudanca.rolaram > 0}
              />
              <Indicador
                rotulo="Período"
                valor={`${dia(rel.mudanca.de)} → ${dia(rel.mudanca.para)}`}
              />
            </div>
          ) : (
            // Dito, e não omitido: a seção existe e explica por que está vazia.
            <p className="tipo-corpo-pequeno text-text-tertiary">
              Esta é a primeira fotografia desta carteira — não há com o que comparar ainda.
            </p>
          )}
        </section>

        <section className="flex flex-col gap-2">
          <p className="tipo-rotulo text-text-tertiary">4 · O que a carteira diz</p>
          {!rel.achados.length ? (
            <p className="tipo-corpo-pequeno text-text-tertiary">
              Nenhuma leitura. Se as empresas ainda não foram enriquecidas pela Receita, não há
              quadro societário — e sem ele não há grupo econômico a ver.
            </p>
          ) : (
            <div className="flex flex-col gap-1">
              {fortes.map((a) => (
                <p key={a.chave} className="tipo-corpo-pequeno text-text">
                  {frase(a, alvoDe(a))}
                </p>
              ))}
              {fracos.length > 0 && (
                <details className="mt-2">
                  <summary className="tipo-rotulo cursor-pointer select-none text-text-secondary">
                    Podem ter outra explicação ({fracos.length})
                  </summary>
                  <div className="mt-1 flex flex-col gap-1">
                    {fracos.map((a) => (
                      <p key={a.chave} className="tipo-corpo-pequeno text-text-secondary">
                        {frase(a, alvoDe(a))}
                      </p>
                    ))}
                  </div>
                </details>
              )}
            </div>
          )}
        </section>
      </CardContent>
    </Card>
  );
}
